import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  Play,
  Shield,
  FileSearch,
  Sparkles,
  Calendar,
  ClipboardCheck,
  BookOpen,
  GraduationCap,
  FileCheck,
  Activity,
  MessageCircle,
  Send,
  X,
  ChevronRight,
  ChevronDown,
  Clock,
  Target,
  Loader2,
  Bot,
  User,
  Zap,
  ExternalLink,
  Download,
  FileText,
  Clipboard,
  Settings,
  BarChart3,
  RotateCcw,
  RefreshCw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default PDCA questions (fallback if backend fails)
const DEFAULT_PDCA_QUESTIONS = {
  plan: {
    title: "Plan",
    description: "Establish BCM objectives and processes",
    color: "blue",
    questions: [
      "How do I create a BCM policy for my organization?",
      "What should be included in a Business Impact Analysis (BIA)?",
      "How do I configure Impact Definitions (High/Medium/Low/None)?",
      "How do I identify critical business processes?",
      "What's the difference between RTO and RPO?",
      "How should I define recovery priorities?",
      "What stakeholders should be involved in BCM planning?",
      "How do I establish a BCM scope for ISO 22301?",
      "How do I use the Dependency Map to visualize assets?",
      "How does AI auto-categorization work during BIA sync?",
      "How do I set up Business Units in my BIA?",
    ]
  },
  do: {
    title: "Do",
    description: "Implement recovery plans and strategies",
    color: "emerald",
    questions: [
      "How do I create a Business Continuity Plan (BCP)?",
      "What should a recovery procedure include?",
      "How do I set up incident response workflows?",
      "What training should BCM team members receive?",
      "How do I document recovery strategies?",
      "What communication plans are needed during incidents?",
      "How do I implement resource management for recovery?",
      "How do I download a PDF report for a recovery plan?",
      "What is the difference between BCP and IT-DRP?",
      "How does the Emergency Response module work?",
      "How do I use the Plan Orchestration Dashboard?",
      "How do I activate an incident from an ERP?",
    ]
  },
  check: {
    title: "Check",
    description: "Monitor and review BCM performance",
    color: "amber",
    questions: [
      "How often should I schedule BCM exercises?",
      "What types of exercises should we conduct?",
      "How do I measure BCM program effectiveness?",
      "What should be included in a post-exercise report?",
      "How do I conduct an internal BCM audit?",
      "What KPIs should I track for BCM compliance?",
      "How do I assess gaps against ISO 22301?",
      "How do I generate a PDF report for exercises or audits?",
      "How do I export the KPI Dashboard as PDF?",
      "How do I send reminders for overdue recovery plans?",
    ]
  },
  act: {
    title: "Act",
    description: "Take actions to continually improve",
    color: "purple",
    questions: [
      "How do I update plans based on exercise findings?",
      "What triggers a BCM plan review cycle?",
      "How should I handle non-conformities?",
      "How do I implement corrective actions?",
      "What's the process for management review?",
      "How do I maintain continuous BCM improvement?",
      "How often should policies be reviewed and updated?",
      "How does AI Root Cause Analysis work for PACA items?",
      "How do I use the AI Dashboard Summary feature?",
      "How do I run a PDCA scan for improvement opportunities?",
    ]
  }
};

// Phase styling configuration (used for both default and custom questions)
const PHASE_STYLES = {
  plan: {
    icon: Clipboard,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
  },
  do: {
    icon: Settings,
    color: "from-emerald-500 to-emerald-600",
    bgColor: "bg-emerald-50",
    borderColor: "border-emerald-200",
    textColor: "text-emerald-700",
  },
  check: {
    icon: BarChart3,
    color: "from-amber-500 to-amber-600",
    bgColor: "bg-amber-50",
    borderColor: "border-amber-200",
    textColor: "text-amber-700",
  },
  act: {
    icon: RotateCcw,
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-700",
  }
};

// Icon mapping
const iconMap = {
  Shield,
  FileSearch,
  Sparkles,
  Calendar,
  ClipboardCheck,
  BookOpen,
  GraduationCap,
  FileCheck,
  Activity,
};

export default function GuidePageNew() {
  const navigate = useNavigate();
  const { user, api } = useAuth();

  // WebAdmin-only page. Non-admins never reach this render (route guard
  // handles it), but keep a component-level redirect as defence-in-depth.
  useEffect(() => {
    if (user && user.role !== "admin") navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleDownloadAdminGuide = async () => {
    try {
      const jwt = localStorage.getItem("gbrms_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/guidebook/download`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(new Blob([blob], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "ResiliencyHUB_Admin_Guidebook.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Guidebook downloaded");
    } catch (e) {
      toast.error("Download failed — admin access required");
    }
  };

  const [selectedRole, setSelectedRole] = useState("bcm_manager");
  const [journeys, setJourneys] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState({});
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `guide-${Date.now()}`);
  const [expandedPDCA, setExpandedPDCA] = useState(null);
  const [pdcaQuestions, setPdcaQuestions] = useState(DEFAULT_PDCA_QUESTIONS);
  const chatEndRef = useRef(null);

  // Helper to get step info (handles both old string format and new object format)
  const getStepInfo = (step) => {
    if (typeof step === 'string') {
      return { name: step, details: null, navigation: null, duration: null };
    }
    return step;
  };

  // Fetch PDCA questions from backend
  useEffect(() => {
    const fetchPDCAQuestions = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ai-assistant/pdca-questions`);
        const data = await response.json();
        if (data.success && data.data) {
          setPdcaQuestions({
            plan: data.data.plan || DEFAULT_PDCA_QUESTIONS.plan,
            do: data.data.do || DEFAULT_PDCA_QUESTIONS.do,
            check: data.data.check || DEFAULT_PDCA_QUESTIONS.check,
            act: data.data.act || DEFAULT_PDCA_QUESTIONS.act,
          });
        }
      } catch (error) {
        console.error("Failed to fetch PDCA questions, using defaults:", error);
        // Keep using default questions
      }
    };
    fetchPDCAQuestions();
  }, []);

  // Fetch journeys based on role
  useEffect(() => {
    const fetchJourneys = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ai-assistant/journey-recommendations/${selectedRole}`);
        const data = await response.json();
        setJourneys(data.journeys || []);
      } catch (error) {
        console.error("Failed to fetch journeys:", error);
        // Fallback journeys
        setJourneys(getDefaultJourneys(selectedRole));
      }
    };
    fetchJourneys();
    setSelectedJourney(null);
    setCurrentStep(0);
  }, [selectedRole]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  // Default journeys fallback
  const getDefaultJourneys = (role) => {
    if (role === "bcm_manager") {
      return [
        { id: "setup-bcm", title: "Set Up My BCM Program", description: "Configure Impact Definitions, authority matrix & templates", icon: "Shield", steps: ["BCM Policy Wizard", "BIA Configuration (Impact Definitions)", "Stakeholder Register"], estimated_time: "2-3 hours", priority: "high" },
        { id: "create-bia", title: "Create a BIA Assessment", description: "Analyze business impact with custom definitions", icon: "FileSearch", steps: ["New BIA", "Impact Matrix (with tooltips)", "Dependencies"], estimated_time: "30-45 min", priority: "high" },
        { id: "generate-plan", title: "Generate a Recovery Plan", description: "AI-powered recovery plans", icon: "Sparkles", steps: ["Select Plan Type", "Define Scope", "Generate"], estimated_time: "20-30 min", priority: "medium" },
      ];
    }
    return [
      { id: "complete-training", title: "Complete My Training", description: "Finish assigned modules", icon: "GraduationCap", steps: ["View Training", "Complete Modules", "Pass Checks"], estimated_time: "1-2 hours", priority: "high" },
      { id: "submit-bia", title: "Submit a BIA Assessment", description: "Hover over categories to see impact definitions", icon: "FileSearch", steps: ["New BIA", "Impact Matrix (hover for help)", "Submit"], estimated_time: "30-45 min", priority: "high" },
    ];
  };

  // Send chat message
  const sendChatMessage = async () => {
    if (!chatInput.trim() || isLoading) return;

    const userMessage = { role: "user", content: chatInput };
    setChatMessages(prev => [...prev, userMessage]);
    setChatInput("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/ai-assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: chatInput,
          session_id: sessionId,
          user_role: selectedRole,
          context: { current_journey: selectedJourney?.id, current_step: currentStep }
        })
      });

      const data = await response.json();
      if (data.success) {
        setChatMessages(prev => [...prev, { role: "assistant", content: data.data.response }]);
      } else {
        setChatMessages(prev => [...prev, { role: "assistant", content: "I'm sorry, I couldn't process that. Please try again." }]);
      }
    } catch (error) {
      setChatMessages(prev => [...prev, { role: "assistant", content: "Connection error. Please check your internet and try again." }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark step as complete
  const markStepComplete = (journeyId, stepIndex) => {
    setCompletedSteps(prev => ({
      ...prev,
      [`${journeyId}-${stepIndex}`]: true
    }));
    if (stepIndex < (selectedJourney?.steps?.length || 0) - 1) {
      setCurrentStep(stepIndex + 1);
    }
  };

  // Calculate progress
  const getJourneyProgress = (journey) => {
    if (!journey?.steps) return 0;
    const completed = journey.steps.filter((_, i) => completedSteps[`${journey.id}-${i}`]).length;
    return Math.round((completed / journey.steps.length) * 100);
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "bg-red-100 text-red-700 border-red-200";
      case "medium": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_cc74fbba-7d42-4e61-a0ea-eedd771fa683/artifacts/7xq5yifk_ResHubNEW1.1-removebg-preview.png" 
                alt="ResiliencyHUB Logo" 
                className="h-[65px] w-auto"
              />
            </Link>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowChatbot(true)}
                className="flex items-center gap-2"
                data-testid="open-chatbot"
              >
                <MessageCircle className="w-4 h-4" />
                Ask AI Guide
              </Button>
              <Link to="/login">
                <Button variant="outline">Sign In</Button>
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-sky-700 via-sky-900 to-slate-900 text-white py-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90 text-sm font-medium mb-6">
            <Zap className="w-4 h-4" />
            AI-Powered Interactive Guide
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            What would you like to do today?
          </h1>
          <p className="text-lg text-sky-100 max-w-2xl mx-auto mb-8">
            Select your goal below and we'll guide you step-by-step. Our AI assistant is here to help!
          </p>

          {/* Role Selector */}
          <div className="flex justify-center">
            <div className="inline-flex bg-white/10 rounded-xl p-1.5">
              <button
                onClick={() => setSelectedRole("bcm_manager")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedRole === "bcm_manager" 
                    ? "bg-white text-sky-700 shadow-lg" 
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
                data-testid="role-bcm-admin"
              >
                <Shield className="w-5 h-5" />
                <span>BCM Manager</span>
              </button>
              <button
                onClick={() => setSelectedRole("process_manager")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedRole === "process_manager" 
                    ? "bg-white text-sky-700 shadow-lg" 
                    : "text-white/80 hover:text-white hover:bg-white/10"
                }`}
                data-testid="role-end-user"
              >
                <User className="w-5 h-5" />
                <span>Business User</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {!selectedJourney ? (
            /* Journey Selection */
            <>
              {/* Documentation & Downloads — internal/dev artifacts, WebAdmin only */}
              {user?.role === "admin" && (
              <Card className="mb-8 border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-purple-50/40 to-slate-50" data-testid="docs-downloads-banner">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
                        <FileCheck className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-slate-900">Documentation &amp; Downloads</CardTitle>
                        <p className="text-xs text-slate-500 mt-0.5">Onboarding &amp; integration artifacts &mdash; latest version (V13)</p>
                      </div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200">NEW: API Integration &amp; Developer Guide</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                    <button
                      type="button"
                      onClick={handleDownloadAdminGuide}
                      className="flex items-start gap-3 p-3 rounded-lg border-2 border-indigo-400 bg-indigo-600 hover:bg-indigo-700 hover:shadow-lg transition-all group text-left"
                      data-testid="doc-banner-admin-pdf"
                    >
                      <Download className="w-5 h-5 text-white flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-white truncate">Admin Guidebook</span>
                          <span className="text-[9px] font-bold text-indigo-700 bg-white px-1 py-0.5 rounded uppercase tracking-wide">PDF</span>
                        </div>
                        <p className="text-xs text-indigo-100 mt-0.5 leading-snug">Download the full admin guide</p>
                      </div>
                    </button>

                    <a
                      href="/UAT_Customer_Onboarding_Guide.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-md transition-all group"
                      data-testid="doc-banner-uat"
                    >
                      <ClipboardCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-800 truncate">UAT Onboarding</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">BCM Managers &amp; Process Owners</p>
                      </div>
                    </a>

                    <a
                      href="/ResiliencyHUB_Demo_Walkthrough_V22.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-sky-200 bg-white hover:border-sky-400 hover:shadow-md transition-all group"
                      data-testid="doc-banner-walkthrough"
                    >
                      <Play className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-800 truncate">Demo Walkthrough</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">Sales &amp; prospect tour</p>
                      </div>
                    </a>

                    <a
                      href="/ResiliencyHUB_Complete_Architectural_Guidebook_V22.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 bg-white hover:border-purple-400 hover:shadow-md transition-all group"
                      data-testid="doc-banner-guidebook"
                    >
                      <BookOpen className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-800 truncate">Architectural Guide</span>
                          <span className="text-[9px] font-bold text-purple-600 bg-purple-100 px-1 py-0.5 rounded">V22</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">Architects &amp; IT leadership</p>
                      </div>
                    </a>

                    <a
                      href="/API_Integration_Developer_Guide.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-600 bg-slate-900 hover:border-slate-400 hover:shadow-lg transition-all group"
                      data-testid="doc-banner-dev-guide"
                    >
                      <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-sm font-semibold text-white truncate">API / Dev Guide</span>
                          <span className="text-[9px] font-bold text-amber-300 bg-amber-900/40 px-1 py-0.5 rounded uppercase tracking-wide">New</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-300 mt-0.5 leading-snug">IT / QA / Integration teams</p>
                      </div>
                    </a>
                  </div>
                </CardContent>
              </Card>
              )}


              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {journeys.map((journey) => {
                const IconComponent = iconMap[journey.icon] || BookOpen;
                const progress = getJourneyProgress(journey);
                return (
                  <Card 
                    key={journey.id}
                    className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] border-2 hover:border-sky-300"
                    onClick={() => { setSelectedJourney(journey); setCurrentStep(0); }}
                    data-testid={`journey-${journey.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center`}>
                          <IconComponent className="w-6 h-6 text-sky-600" />
                        </div>
                        <Badge className={getPriorityColor(journey.priority)}>
                          {journey.priority === "high" ? "Recommended" : journey.priority}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">{journey.title}</h3>
                      <p className="text-sm text-slate-600 mb-4">{journey.description}</p>
                      <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-4 h-4" />
                          {journey.estimated_time}
                        </span>
                        <span className="flex items-center gap-1 text-slate-500">
                          <Target className="w-4 h-4" />
                          {journey.steps?.length || 0} steps
                        </span>
                      </div>
                      {progress > 0 && (
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-slate-600">Progress</span>
                            <span className="text-sky-600 font-medium">{progress}%</span>
                          </div>
                          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-sky-500 rounded-full transition-all"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            </>
          ) : (
            /* Journey Detail View */
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Steps Panel */}
              <div className="lg:col-span-2">
                <div className="flex items-center gap-4 mb-6">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedJourney(null)}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Goals
                  </Button>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-slate-900">{selectedJourney.title}</h2>
                    <p className="text-sm text-slate-600">{selectedJourney.description}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mb-8">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-slate-600">Your Progress</span>
                    <span className="text-sky-600 font-semibold">{getJourneyProgress(selectedJourney)}% Complete</span>
                  </div>
                  <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-sky-700 to-teal-600 rounded-full transition-all duration-500"
                      style={{ width: `${getJourneyProgress(selectedJourney)}%` }}
                    />
                  </div>
                </div>

                {/* Steps List */}
                <div className="space-y-4">
                  {selectedJourney.steps?.map((step, index) => {
                    const stepInfo = getStepInfo(step);
                    const isCompleted = completedSteps[`${selectedJourney.id}-${index}`];
                    const isCurrent = index === currentStep;
                    return (
                      <Card 
                        key={index}
                        className={`transition-all ${
                          isCurrent ? "ring-2 ring-sky-700 shadow-lg" : ""
                        } ${isCompleted ? "bg-emerald-50 border-emerald-200" : ""}`}
                      >
                        <CardContent className="py-4 px-6">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                              isCompleted 
                                ? "bg-emerald-500 text-white" 
                                : isCurrent 
                                  ? "bg-sky-700 text-white" 
                                  : "bg-slate-200 text-slate-600"
                            }`}>
                              {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className={`font-medium ${isCompleted ? "text-emerald-700" : "text-slate-900"}`}>
                                  {stepInfo.name}
                                </h4>
                                {stepInfo.duration && (
                                  <Badge variant="outline" className="text-xs">
                                    <Clock className="w-3 h-3 mr-1" />
                                    {stepInfo.duration}
                                  </Badge>
                                )}
                              </div>
                              {/* Show inline instructions for current step */}
                              {isCurrent && stepInfo.details && (
                                <p className="text-sm text-slate-500 mt-2 bg-sky-50 p-3 rounded-md border border-sky-100">
                                  <span className="font-medium text-sky-700">How to: </span>
                                  {stepInfo.details}
                                </p>
                              )}
                              {isCurrent && !stepInfo.details && (
                                <p className="text-sm text-slate-500 mt-1">
                                  Click "Mark Complete" when you've finished this step
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {stepInfo.navigation && isCurrent && !isCompleted && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => navigate(stepInfo.navigation)}
                                  className="border-sky-300 text-sky-600"
                                >
                                  <ExternalLink className="w-4 h-4 mr-1" />
                                  Open
                                </Button>
                              )}
                              {isCurrent && !isCompleted && (
                                <Button
                                  size="sm"
                                  onClick={() => markStepComplete(selectedJourney.id, index)}
                                  className="bg-sky-700 hover:bg-sky-800"
                                >
                                  Mark Complete
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              )}
                              {isCompleted && (
                                <Badge className="bg-emerald-100 text-emerald-700">Completed</Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Completion Message */}
                {getJourneyProgress(selectedJourney) === 100 && (
                  <Card className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
                    <CardContent className="py-6 text-center">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-3" />
                      <h3 className="text-xl font-bold mb-2">Journey Complete! 🎉</h3>
                      <p className="text-emerald-100 mb-4">
                        You've successfully completed all steps in this journey.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={() => setSelectedJourney(null)}
                      >
                        Choose Another Goal
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* AI Help Card */}
                <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-sky-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center">
                        <Bot className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-slate-900">Need Help?</h4>
                        <p className="text-sm text-slate-600">Ask our AI assistant</p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowChatbot(true)}
                      className="w-full bg-sky-700 hover:bg-sky-800"
                    >
                      <MessageCircle className="w-4 h-4 mr-2" />
                      Chat with AI Guide
                    </Button>
                  </CardContent>
                </Card>

                {/* Quick Links */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Quick Links</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <Link to="/guide" className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors">
                      <BookOpen className="w-4 h-4 text-sky-600" />
                      <span className="text-sm text-slate-700">Full Feature Guide</span>
                    </Link>
                    <button onClick={handleDownloadAdminGuide} className="w-full text-left flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="download-admin-guide-btn">
                      <FileCheck className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm text-slate-700">Download Admin Guide</span>
                    </button>
                    <Link to="/request-demo" className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-100 transition-colors">
                      <Play className="w-4 h-4 text-purple-600" />
                      <span className="text-sm text-slate-700">Request Enquiry &amp; Demo</span>
                    </Link>
                  </CardContent>
                </Card>

                {/* Documentation & Downloads — internal/dev artifacts, WebAdmin only */}
                {user?.role === "admin" && (
                <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/40 to-purple-50/30" data-testid="docs-downloads-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileCheck className="w-5 h-5 text-indigo-600" />
                      Documentation &amp; Downloads
                    </CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Official onboarding &amp; integration artifacts &mdash; latest version (V13)</p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <a
                      href="/UAT_Customer_Onboarding_Guide.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-emerald-200 bg-white hover:border-emerald-400 hover:shadow-sm transition-all group"
                      data-testid="doc-link-uat"
                    >
                      <ClipboardCheck className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">UAT Customer Onboarding Guide</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">For BCM Managers &amp; Process Owners &mdash; TurboTax-style UI walkthrough</p>
                      </div>
                    </a>

                    <a
                      href="/ResiliencyHUB_Demo_Walkthrough_V22.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-sky-200 bg-white hover:border-sky-400 hover:shadow-sm transition-all group"
                      data-testid="doc-link-walkthrough"
                    >
                      <Play className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">Demo Walkthrough</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">For sales &amp; prospects &mdash; feature showcase with screenshots</p>
                      </div>
                    </a>

                    <a
                      href="/ResiliencyHUB_Complete_Architectural_Guidebook_V22.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-purple-200 bg-white hover:border-purple-400 hover:shadow-sm transition-all group"
                      data-testid="doc-link-guidebook"
                    >
                      <BookOpen className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800">Architectural Guidebook <span className="text-[10px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded ml-1">V22</span></span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">For enterprise architects &amp; IT leadership &mdash; full system design</p>
                      </div>
                    </a>

                    <a
                      href="/API_Integration_Developer_Guide.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-3 p-3 rounded-lg border border-slate-300 bg-slate-900 hover:border-slate-500 hover:shadow-md transition-all group"
                      data-testid="doc-link-dev-guide"
                    >
                      <Zap className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-white">API Integration &amp; Developer Guide</span>
                          <span className="text-[9px] font-bold text-amber-300 bg-amber-900/40 px-1.5 py-0.5 rounded uppercase tracking-wide">New</span>
                          <ExternalLink className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                        <p className="text-xs text-slate-300 mt-0.5">For IT / QA / Integration engineers &mdash; REST contracts, payloads, curl smoke test</p>
                      </div>
                    </a>
                  </CardContent>
                </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* AI Chatbot Modal */}
      {showChatbot && (
        <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg h-[600px] flex flex-col shadow-2xl animate-in slide-in-from-bottom-4">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-sky-700 to-teal-600 text-white rounded-t-lg">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold">ResiliencyHUB Guide Assistant</h3>
                  <p className="text-xs text-sky-200">Ask me anything about BCM!</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {chatMessages.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setChatMessages([]);
                      setChatInput("");
                      setExpandedPDCA(null);
                    }}
                    className="text-white hover:bg-white/20 flex items-center gap-1"
                    data-testid="new-chat-btn"
                    title="Start new chat"
                  >
                    <RefreshCw className="w-4 h-4" />
                    <span className="text-xs">New Chat</span>
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowChatbot(false)}
                  className="text-white hover:bg-white/20"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Chat Messages */}
            <ScrollArea className="flex-1 p-4">
              {chatMessages.length === 0 && (
                <div className="py-4">
                  <div className="text-center mb-6">
                    <Bot className="w-10 h-10 mx-auto text-sky-400 mb-2" />
                    <p className="text-slate-600 text-sm">Hi! I'm your BCM guide. Select a topic or ask me anything!</p>
                  </div>
                  
                  {/* PDCA Categories */}
                  <div className="space-y-3">
                    {Object.entries(pdcaQuestions).map(([key, category]) => {
                      const styles = PHASE_STYLES[key];
                      const IconComponent = styles.icon;
                      const isExpanded = expandedPDCA === key;
                      return (
                        <div key={key} className={`rounded-lg border ${styles.borderColor} overflow-hidden`}>
                          {/* Category Header */}
                          <button
                            onClick={() => setExpandedPDCA(isExpanded ? null : key)}
                            className={`w-full flex items-center justify-between p-3 ${styles.bgColor} hover:opacity-90 transition-all`}
                            data-testid={`pdca-${key}-toggle`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${styles.color} flex items-center justify-center`}>
                                <IconComponent className="w-4 h-4 text-white" />
                              </div>
                              <div className="text-left">
                                <h4 className={`font-semibold text-sm ${styles.textColor}`}>{category.title}</h4>
                                <p className="text-xs text-slate-500">{category.description}</p>
                              </div>
                            </div>
                            <ChevronDown className={`w-4 h-4 ${styles.textColor} transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </button>
                          
                          {/* Questions List */}
                          {isExpanded && (
                            <div className="p-2 bg-white space-y-1">
                              {category.questions?.map((q, i) => (
                                <button
                                  key={i}
                                  onClick={() => { setChatInput(q); setExpandedPDCA(null); }}
                                  className="block w-full text-left px-3 py-2 rounded-md hover:bg-slate-100 text-sm text-slate-700 transition-colors"
                                  data-testid={`pdca-${key}-question-${i}`}
                                >
                                  {q}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Quick tip */}
                  <p className="text-xs text-slate-400 text-center mt-4">
                    Tip: Click a category above or type your own question below
                  </p>
                </div>
              )}
              {chatMessages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 mb-4 ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  {msg.role === "assistant" && (
                    <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-sky-600" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] px-4 py-2 rounded-2xl ${
                      msg.role === "user"
                        ? "bg-sky-700 text-white rounded-br-md"
                        : "bg-slate-100 text-slate-800 rounded-bl-md"
                    }`}
                  >
                    {msg.role === "assistant" ? (
                      <div className="text-sm prose prose-sm prose-slate max-w-none">
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </div>
                    ) : (
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    )}
                  </div>
                  {msg.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3 mb-4">
                  <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-sky-600" />
                  </div>
                  <div className="bg-slate-100 px-4 py-2 rounded-2xl rounded-bl-md">
                    <Loader2 className="w-5 h-5 animate-spin text-sky-600" />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </ScrollArea>

            {/* Chat Input */}
            <div className="p-4 border-t bg-slate-50 rounded-b-lg">
              <div className="flex gap-2">
                <Input
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && sendChatMessage()}
                  placeholder="Type your question..."
                  className="flex-1"
                  disabled={isLoading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={sendChatMessage}
                  disabled={!chatInput.trim() || isLoading}
                  className="bg-sky-700 hover:bg-sky-800"
                  data-testid="send-chat"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
